import { Component, OnInit, Input} from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';

import { environment } from '../../environments/environment';
import { IRoughcheck, IAssessment, ISection, IQuestion, IRisk, IReward } from '../roughcheck';
import { MessageService } from '../message.service';
import { RoughcheckService } from '../roughcheck.service';
import { LoggerService } from '../logger.service';

@Component({
  selector: 'app-question',
  templateUrl: './question.component.html',
})
export class QuestionComponent implements OnInit {

  @Input() question: IQuestion = {
    section: 0,
    id: 0,
    xref: '00000000-0000-0000-0000-000000000000',
    title: '',
    description: '',
    risk_id: 0,
    risk_trigger: 0,
    reward_id: 0,
    reward_trigger: 0,
    progress: 0,
    notes: '',
  };
  risks: IRisk[];
  rewards: IReward[];
  roughcheck: IRoughcheck = {
    rref: '',
    title: '',
    image: '',
    image_alt: '',
    category: '',
    riskprofile: '',
    rewardprofile: '',
    show_risk_reward: 1,
    description: '',
    slug: '',
    status: 0,
    status_email: 0,
    feedback: 0,
    sponsor: '',
    website: ''
  };

  questionForm = this.fb.group({
    risk: [''],
    reward: [''],
    progress: [0],
    notes: [''],
  }, {updateOn: 'changed'});

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private service: RoughcheckService,
    private message: MessageService,
    private logger: LoggerService,
  ) { }

  ngOnInit(): void {
    this.risks = this.service.getRisks();
    this.rewards = this.service.getRewards();
    this.questionForm.controls.risk.setValue(this.question.risk_id);
    this.questionForm.controls.reward.setValue(this.question.reward_id);
    this.roughcheck = this.service.getRoughcheck();
  }

  /**
   * Set the question then show the notes dialogue
   */
  onNotesClick(): void {
    this.service.setQuestion(this.question);
    this.router.navigate([
      '/a',
      this.service.getRRef(),
      this.service.getARef(),
      'section', this.question.section, this.question.id,
      'notes'
    ]);
  }

  /**
   * Set the question then show the notes dialogue
   */
  onFeedbackClick(): void {
    this.service.setQuestion(this.question);
    this.router.navigate([
      '/a',
      this.service.getRRef(),
      this.service.getARef(),
      'section', this.question.section, this.question.id,
      'feedback'
    ]);
  }

  /**
   * Fetch and show help
   */
  onHelpClick(): void {
    if (! this.question) { return; }
    this.service.setQuestion(this.question);
    this.router.navigateByUrl(this.router.url + '(popup:questionhelp)');
  }

  /**
   * Update progress bar changes
   */
  OnProgressChange(value): void {
    this.question.progress = value;
    this.Save();
  }

  /**
   * Record changes to the progress bar
   */
  Save(): void {
    this.service.setHttpQuestion(this.question)
        .subscribe(
          (resp) => this.handleSuccess(resp),
          (error) => this.service.handleError(error)
        );
  }

  /**
   * Compare Risk and Reward ids
   */
  compareRiskRewardFn(r1: number, r2: number): boolean {
    if (r1 === r2) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * React to and save risk changes
   */
  onRiskChanged(data): void {
    const index = data.split(':')[0];
    this.question.risk_id = this.risks[index].id;
    this.Save();
  }

  /**
   * React to and save reward changes
   */
  onRewardChanged(data): void {
    const index = data.split(':')[0];
    this.question.reward_id = this.rewards[index].id;
    this.Save();
  }

  /**
   * Risk/Reward display order
   * ROUGHCHECK_RR_NONE = 0
   * ROUGHCHECK_RR_RISK_REWARD = 1
   * ROUGHCHECK_RR_REWARD_RISK = 2
   * ROUGHCHECK_RR_RISK = 3
   * ROUGHCHECK_RR_REWARD = 4
   */
  isRiskFirst(): boolean {
    if (this.roughcheck.show_risk_reward === 1) {
      return true;
    } else {
      return false;
    }
  }
  /**
   * Size for Risk/Reward columns
   * If one then 2 else 4
   * If none then not displayed
   */
  sizeRiskRewardCols(): string {
    if (this.roughcheck.show_risk_reward === 0) {
      return '2';
    } else if (this.roughcheck.show_risk_reward === 1) {
      return '4';
    } else if (this.roughcheck.show_risk_reward === 2) {
      return '4';
    } else if (this.roughcheck.show_risk_reward === 3) {
      return '2';
    } else if (this.roughcheck.show_risk_reward === 4) {
      return '2';
    } else {
      return '4';
    }
  }
  /**
   * Size for Risk/Reward select
   * If one then 2 else 4
   * If none then not displayed
   */
  sizeRiskRewardSelect(): string {
    if (this.roughcheck.show_risk_reward === 0) {
      return '6';
    } else if (this.roughcheck.show_risk_reward === 1) {
      return '6';
    } else if (this.roughcheck.show_risk_reward === 2) {
      return '6';
    } else if (this.roughcheck.show_risk_reward === 3) {
      return '12';
    } else if (this.roughcheck.show_risk_reward === 4) {
      return '12';
    } else {
      return '12';
    }
  }
  /**
   * Hide Risk
   */
  hideRisk(): boolean {
    if (this.roughcheck.show_risk_reward === 0) {
      return true;
    } else if (this.roughcheck.show_risk_reward === 4) {
      return true;
    } else {
      return false;
    }
  }
  /**
   * Hide Reward
   */
  hideReward(): boolean {
    if (this.roughcheck.show_risk_reward === 0) {
      return true;
    } else if (this.roughcheck.show_risk_reward === 3) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Hide Reward
   */
  hideFeedback(): boolean {
    if (this.roughcheck.feedback === 1) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * Populate Sections then go to the first one
   */
  private handleSuccess(resp: HttpResponse<any>) {
    const question: IQuestion = resp.body;
    this.question = question;
  }

}
